/*
  ========================================================================
  File: assets/lm-collection.js - collection page behaviour (module lm-collection)
  LAST UPDATED: 25/09/2026
  ========================================================================
  Loaded as a module by sections/main-collection.liquid (edit lm-script), so
  it runs once per page however often Prestige re-renders the section after
  a filter change. Everything is delegated from document or lives in a
  custom element, so it keeps working after those re-renders.

  1. Size picks in-stock only (Collection pages redesign, desktop note: "Sold-
     out sizes are hidden when a size filter is on"). Shopify matches variant
     filters per variant, so size 10 + filter.v.availability=1 means "has an
     in-stock size 10". When the first size is ticked, "In stock only" is
     ticked with it; when the last size is cleared (checkbox or its chip), the
     availability filter goes too, but only if this script added it. A page
     that opens with a size but no availability (a shared or old link) is
     brought in line once, in place, without scrolling.
     Without JavaScript nothing is added: the size filter then also shows
     styles whose size is sold out, and "In stock only" stays one tap away.

  2. <lm-load-more>: "Load more" appends the next page's product cards in
     place through the Section Rendering API (?section_id=), so the scroll
     position, filters and sort (all in the URL) are kept, and "Showing X of N"
     is updated and announced. The button is a real ?page= link, and the
     numbered pages stay in the markup for browsers without JavaScript
     (@media (scripting: none) in lm-collection.css) and show on a later
     ?page= opened directly, until the first Load more hides them.

  3. The count after a filter change is announced through one polite
     status region outside the section (Prestige replaces the section).
  ========================================================================
*/

const AVAILABILITY = 'filter.v.availability';
const SIZE_INPUT = 'input[data-lm-size-filter]';

/* True while the availability filter is on because a size was picked, not
   because the shopper chose "In stock only" themselves. Module state, so it
   survives Prestige replacing the section after each filter change. */
let availabilityAddedForSize = false;

const availabilityInput = (form) => form?.querySelector(`input[name="${AVAILABILITY}"]:not([disabled])`);

/* Capture phase on document runs before Prestige's facets-form "change"
   listener, so the form it serialises already has the right availability. */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.form) return;

  if (input.name === AVAILABILITY) {
    availabilityAddedForSize = false;
    return;
  }
  if (!input.matches(SIZE_INPUT)) return;

  const availability = availabilityInput(input.form);
  if (!availability) return;

  const anySize = input.form.querySelector(`${SIZE_INPUT}:checked`) !== null;
  if (anySize && !availability.checked) {
    availability.checked = true;
    availabilityAddedForSize = true;
  } else if (!anySize && availability.checked && availabilityAddedForSize) {
    availability.checked = false;
    availabilityAddedForSize = false;
  }
}, true);

/* Removing the last size chip (active-facets marks it data-lm-last-size) also
   drops the availability filter this script added. Prestige's facet-link reads
   the link's href on click, after this capture-phase listener. */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[data-lm-last-size]') : null;
  if (!link || !availabilityAddedForSize) return;
  const url = new URL(link.href, window.location.href);
  url.searchParams.delete(AVAILABILITY);
  link.href = url.toString();
  availabilityAddedForSize = false;
}, true);

/* A page opened with a size filter but without availability. */
const alignOnLoad = async () => {
  const url = new URL(window.location.href);
  if (url.searchParams.has(AVAILABILITY)) return;
  const form = document.querySelector(`facets-form form:has(${SIZE_INPUT}:checked)`);
  const availability = availabilityInput(form);
  const sectionId = form?.closest('facets-form')?.getAttribute('section-id');
  const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
  if (!availability || !section) return;

  url.searchParams.set(AVAILABILITY, '1');
  url.searchParams.delete('page');
  const request = new URL(url);
  request.searchParams.set('section_id', sectionId);
  try {
    const response = await fetch(request.toString());
    if (!response.ok) return;
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = html.querySelector('.shopify-section');
    if (!fresh) return;
    /* Keep the filter groups the shopper has open or closed since the page loaded. */
    const openState = new Map([...section.querySelectorAll('facets-form details')].map((d) => [d.querySelector('input[name]')?.name, d.open]));
    fresh.querySelectorAll('facets-form details').forEach((details) => {
      const name = details.querySelector('input[name]')?.name;
      if (openState.has(name)) details.open = openState.get(name);
    });
    section.replaceChildren(...document.importNode(fresh, true).childNodes);
    window.history.replaceState(window.history.state, '', url.toString());
    availabilityAddedForSize = true;
  } catch {
    /* The page as served is still correct, just not narrowed to in-stock sizes. */
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', alignOnLoad, { once: true });
} else {
  alignOnLoad();
}

/* 3. The count reacts to the filter (Collection pages redesign, callout 3) and screen readers hear
   it. Prestige replaces the whole section after each filter change (facet:update), so a live region
   inside it would be a new node each time, which screen readers do not reliably read. One polite
   region outside the section repeats the count as displayed (innerText: the mobile or desktop
   wording, whichever shows) once the new section is in. */
const countRegion = () => {
  let region = document.getElementById('lm-collection-count-live');
  if (!region) {
    region = document.createElement('p');
    region.id = 'lm-collection-count-live';
    region.className = 'sr-only';
    region.setAttribute('role', 'status');
    document.body.append(region);
  }
  return region;
};

/* Created up front, empty: a region that already exists when its text changes is the one that is read. */
countRegion();

document.addEventListener('facet:update', (event) => {
  const sectionId = event.detail?.url?.searchParams?.get('section_id');
  const section = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null;
  if (!section) return;
  const observer = new MutationObserver(() => {
    observer.disconnect();
    const count = section.querySelector('.lm-collection-count');
    if (count) countRegion().textContent = count.innerText.trim();
  });
  observer.observe(section, { childList: true });
});

class LmLoadMore extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-lm-load-more-next]') : null;
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    event.preventDefault();
    this.load(link);
  };

  label(first, last, total) {
    const template = first > 1 ? this.dataset.labelRange : this.dataset.label;
    return (template ?? '').replace('[shown]', last).replace('[from]', first).replace('[to]', last).replace('[total]', total);
  }

  /* Once pages are joined in place, numbered page links describe none of them (page 2 would be
     marked current with pages 1 and 2 both on screen), so they are hidden from the first Load more
     on, including on a later ?page= opened directly, where they showed until now (--always). The
     server's copy in the response is never imported: it was rendered as page 2+, so it carries
     --always. Without JavaScript this never runs and the links stay (lm-collection.css). */
  hidePages() {
    this.querySelectorAll('.lm-load-more__pages').forEach((pages) => {
      pages.classList.remove('lm-load-more__pages--always');
      pages.hidden = true;
    });
  }

  async load(link) {
    if (this.hasAttribute('aria-busy')) return;
    const sectionId = this.getAttribute('section-id');
    const list = document.getElementById(`product-list-${sectionId}`);
    if (!list) {
      window.location.href = link.href;
      return;
    }

    const request = new URL(link.href, window.location.href);
    request.searchParams.set('section_id', sectionId);
    this.setAttribute('aria-busy', 'true');
    link.setAttribute('aria-disabled', 'true');

    try {
      const response = await fetch(request.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incomingList = html.getElementById(`product-list-${sectionId}`);
      const incomingMore = html.querySelector('lm-load-more');
      if (!incomingList) throw new Error('no product list in the response');

      /* Product cards only: a collection promo block is placed per page and would repeat. */
      const cards = [...incomingList.children].filter((el) => el.localName === 'product-card').map((el) => document.importNode(el, true));
      const reveal = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
      cards.forEach((card, index) => {
        card.removeAttribute('reveal-on-scroll');
        if (reveal) card.animate([{ opacity: 0, transform: 'translateY(20px)' }, { opacity: 1, transform: 'none' }], { duration: 200, delay: index * 30, easing: 'ease-in-out', fill: 'backwards' });
      });
      list.append(...cards);

      const last = Number(incomingMore?.dataset.last ?? (Number(this.dataset.last) + cards.length));
      this.dataset.last = String(last);
      const status = this.querySelector('.lm-load-more__status');
      if (status) status.textContent = this.label(Number(this.dataset.first), last, this.dataset.total);

      const next = incomingMore?.querySelector('a[data-lm-load-more-next]');
      if (next) {
        link.href = next.getAttribute('href');
        link.removeAttribute('aria-disabled');
      } else {
        link.remove();
      }

      this.hidePages();

      /* Keyboard and screen reader users continue from the first new style. */
      cards[0]?.querySelector('a[href]')?.focus({ preventScroll: true });
    } catch {
      /* Fall back to the plain ?page= link. */
      window.location.href = link.href;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!window.customElements.get('lm-load-more')) {
  window.customElements.define('lm-load-more', LmLoadMore);
}
